/**
 * InvoiceDetail Component
 * 
 * Displays the full details of a single invoice.
 * Polish accounting software style with:
 *   - Clean invoice card with multi-column details
 *   - Customer info with blue link styling
 *   - Line items table
 *   - Action panel with icon buttons
 */
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faPen, 
  faTrash, 
  faCheck,
  faDollarSign,
  faCopy
} from '@fortawesome/free-solid-svg-icons';

import { useApi } from 'api';
import { Customer, InvoiceLine } from 'types';
import { 
  StatusBadge, 
  getPaidBadge, 
  getFinalizedBadge, 
  ConfirmModal, 
  LoadingSpinner,
  InvoiceTotals,
} from '../common';
import { formatAmount, parseInvoiceTotal, parseInvoiceTax, formatDate } from '../../utils/invoiceUtils';
import { logger } from '../../utils/logger';
import { useInvoiceActions, useNavigationState, useCustomerFetch } from 'app/hooks';

/**
 * Invoice data from the API
 */
interface InvoiceData {
  id: number;
  customer_id: number | null;
  finalized: boolean;
  paid: boolean;
  date: string | null;
  deadline: string | null;
  total: string | null;
  tax: string | null;
  invoice_lines: InvoiceLine[];
}

const InvoiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const api = useApi();
  
  // Navigation state for preserving list filters when going back
  const { backToListUrl, buildEditUrl, buildNewUrl } = useNavigationState();

  // State
  const [invoice, setInvoice] = useState<InvoiceData | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Request ID ref to prevent race conditions
  const latestRequestIdRef = useRef(0);

  // Customer fetching utility for getting customer data
  const { fetchCustomerById } = useCustomerFetch();

  /**
   * Fetch invoice details
   * 
   * Always fetches fresh data from the single invoice API endpoint.
   * Uses request ID guard to prevent race conditions.
   * 
   * NOTE: We intentionally do NOT use the list endpoint with id filter because:
   * 1. The API ignores the id filter (documented limitation)
   * 2. The list endpoint may return cached/stale data
   * 3. Using getInvoice directly is guaranteed to return the correct, fresh invoice
   */
  const fetchInvoice = useCallback(async () => {
    if (!id) return;

    const invoiceId = parseInt(id, 10);
    
    // Generate a new request ID for this fetch
    const requestId = ++latestRequestIdRef.current;

    // Always fetch fresh data from API to ensure consistency
    setIsLoading(true);
    setError(null);

    try {
      // Use the single invoice endpoint - guaranteed to return correct invoice
      const { data } = await api.getInvoice({ id: invoiceId });
      
      // Only update state if this is still the latest request
      if (requestId !== latestRequestIdRef.current) {
        return; // Discard stale response
      }
      
      setInvoice(data);
      
      // Check if customer data is already included in the response
      const embeddedCustomer = (data as any).customer as Customer | undefined;
      
      if (embeddedCustomer && embeddedCustomer.id && embeddedCustomer.id === data.customer_id) {
        // Customer data is embedded and valid
        setCustomer(embeddedCustomer);
      } else if (data.customer_id) {
        // Fetch customer via customer_id filter (guaranteed to work)
        const foundCustomer = await fetchCustomerById(data.customer_id);
        
        // Check again after async customer fetch
        if (requestId !== latestRequestIdRef.current) {
          return;
        }
        setCustomer(foundCustomer);
      } else {
        setCustomer(null);
      }
    } catch (err) {
      if (requestId !== latestRequestIdRef.current) {
        return;
      }
      setError('Failed to load invoice. Please try again.');
      logger.error('Error fetching invoice:', err);
    } finally {
      if (requestId === latestRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [api, id, fetchCustomerById]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  // Invoice actions (delete, finalize, toggle paid) - DRY hook
  const { deleteAction, finalizeAction, togglePaid, isTogglingPaid } = useInvoiceActions({
    onDeleteSuccess: () => navigate(backToListUrl),
    onFinalizeSuccess: fetchInvoice,
    onTogglePaidSuccess: fetchInvoice,
  });

  /**
   * Handle edit click (preserve the from params)
   */
  const handleEdit = () => {
    // Navigate to edit form - it will fetch fresh data from API
    navigate(buildEditUrl(parseInt(id!, 10)));
  };

  /**
   * Toggle paid status
   */
  const handleTogglePaid = () => {
    if (!invoice) return;
    togglePaid(invoice.id, invoice.paid);
  };

  /**
   * Handle clone - navigate to new invoice form with cloned data
   */
  const handleClone = () => {
    if (!invoice) return;
    
    // Build clone data including customer info
    const cloneData = {
      ...invoice,
      customer,
    };
    
    navigate(buildNewUrl(), {
      state: { cloneFrom: cloneData }
    });
  };

  /**
   * Format string amount to currency
   */
  const formatStringAmount = (amount: string | null | undefined): string => {
    if (!amount) return '€0.00';
    return formatAmount(parseFloat(amount));
  };

  // Memoize calculations to avoid recalculating on every render
  // Must be before early returns to follow React hooks rules
  const { total, tax, subtotal } = useMemo(() => {
    if (!invoice) {
      return { total: 0, tax: 0, subtotal: 0 };
    }
    const calcTotal = parseInvoiceTotal(invoice.total, invoice.invoice_lines);
    const calcTax = parseInvoiceTax(invoice.tax, invoice.invoice_lines);
    return {
      total: calcTotal,
      tax: calcTax,
      subtotal: calcTotal - calcTax,
    };
  }, [invoice]);

  // Loading state
  if (isLoading) {
    return <LoadingSpinner message="Loading invoice..." />;
  }

  // Error state
  if (error || !invoice) {
    return (
      <div className="card">
        <div className="card-body">
          <div className="empty-state">
            <p className="empty-state-title">{error || 'Invoice not found'}</p>
            <Link to={backToListUrl} className="btn btn-primary mt-3">
              Back to Invoices
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const paidBadge = getPaidBadge(invoice.paid);
  const finalizedBadge = getFinalizedBadge(invoice.finalized);

  /**
   * Build URL to filter invoices by customer
   * Uses customerId and customerName params as expected by InvoicesList
   */
  const getCustomerFilterUrl = (cust: Customer): string => {
    const params = new URLSearchParams();
    params.set('customerId', String(cust.id));
    params.set('customerName', `${cust.first_name} ${cust.last_name}`);
    return `/?${params.toString()}`;
  };

  return (
    <>
      {/* Back Link */}
      <Link to={backToListUrl} className="btn btn-ghost mb-3">
        <FontAwesomeIcon icon={faArrowLeft} />{' '}
        Back to Invoices
      </Link>

      {/* Invoice Card */}
      <div className="invoice-card">
        {/* Invoice Header */}
        <div className="invoice-card-header">
          <h1 className="invoice-card-title">
            Invoice nr {invoice.id}/{new Date().getFullYear()}
          </h1>
          <div className="invoice-header-badges">
            <StatusBadge {...finalizedBadge} />
            <StatusBadge {...paidBadge} />
          </div>
        </div>

        {/* Invoice Details Grid - 3 columns */}
        <div className="invoice-details-grid">
          {/* Left Column - Customer */}
          <div className="invoice-details-column">
            <div className="invoice-detail-row">
              <span className="invoice-detail-label">Customer</span>
              {customer ? (
                <div className="invoice-detail-value">
                  <Link 
                    to={getCustomerFilterUrl(customer)} 
                    className="customer-link"
                  >
                    {customer.first_name} {customer.last_name}
                  </Link>
                  <div className="customer-address">
                    {customer.address}<br />
                    {customer.zip_code} {customer.city}<br />
                    {customer.country}
                  </div>
                </div>
              ) : (
                <span className="invoice-detail-value">
                  Customer #{invoice.customer_id}
                </span>
              )}
            </div>
          </div>

          {/* Middle Column - Dates */}
          <div className="invoice-details-column">
            <div className="invoice-detail-row">
              <span className="invoice-detail-label">Invoice Date</span>
              <span className="invoice-detail-value">{formatDate(invoice.date)}</span>
            </div>
            <div className="invoice-detail-row">
              <span className="invoice-detail-label">Due Date</span>
              <span className="invoice-detail-value">{formatDate(invoice.deadline)}</span>
            </div>
          </div>

          {/* Right Column - Additional Info */}
          <div className="invoice-details-column">
            <div className="invoice-detail-row">
              <span className="invoice-detail-label">Status</span>
              <span className="invoice-detail-value">{invoice.finalized ? 'Finalized' : 'Draft'}</span>
            </div>
            <div className="invoice-detail-row">
              <span className="invoice-detail-label">Payment</span>
              <span className="invoice-detail-value">{invoice.paid ? 'Paid' : 'Unpaid'}</span>
            </div>
            <div className="invoice-detail-row">
              <span className="invoice-detail-label">Currency</span>
              <span className="invoice-detail-value">EUR</span>
            </div>
          </div>
        </div>

        {/* Line Items Table */}
        <div className="invoice-lines-section">
          <table className="invoice-lines-table">
            <thead>
              <tr>
                <th className="line-col-num"></th>
                <th className="line-col-product">Product</th>
                <th className="line-col-qty">Qty</th>
                <th className="line-col-unit">Unit</th>
                <th className="line-col-price">Unit Price (EUR)</th>
                <th className="line-col-total">Total (EUR)</th>
                <th className="line-col-vat">VAT</th>
              </tr>
            </thead>
            <tbody>
              {invoice.invoice_lines.map((line, index) => (
                <tr key={line.id}>
                  <td className="line-col-num">{index + 1}</td>
                  <td className="line-col-product">
                    <div className="line-product-name">{line.label}</div>
                  </td>
                  <td className="line-col-qty">{line.quantity}</td>
                  <td className="line-col-unit">{line.unit}</td>
                  <td className="line-col-price">{formatStringAmount(line.product?.unit_price_without_tax).replace('€', '').trim()}</td>
                  <td className="line-col-total">{formatStringAmount(line.price).replace('€', '').trim()}</td>
                  <td className="line-col-vat">{line.vat_rate} %</td>
                </tr>
              ))}
              {invoice.invoice_lines.length === 0 && (
                <tr>
                  <td colSpan={7} className="empty-lines">
                    No line items
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Totals Summary */}
        <InvoiceTotals
          subtotal={subtotal}
          tax={tax}
          total={total}
          showTaxPercentage={false}
        />
      </div>

      {/* Action Panel - Only show API-supported actions */}
      <div className="action-panel">
        {!invoice.finalized && (
          <button className="action-panel-btn" onClick={handleEdit}>
            <div className="action-panel-icon">
              <FontAwesomeIcon icon={faPen} />
            </div>
            <span className="action-panel-label">Edit</span>
          </button>
        )}

        {/* Clone - available for all invoices */}
        <button className="action-panel-btn" onClick={handleClone}>
          <div className="action-panel-icon">
            <FontAwesomeIcon icon={faCopy} />
          </div>
          <span className="action-panel-label">Clone</span>
        </button>

        {/* Mark as Paid/Unpaid - available for all invoices */}
        <button 
          type="button"
          className="action-panel-btn" 
          onClick={handleTogglePaid}
          disabled={isTogglingPaid}
        >
          <div className="action-panel-icon">
            <FontAwesomeIcon icon={faDollarSign} />
          </div>
          <span className="action-panel-label">
            {isTogglingPaid ? 'Updating...' : (invoice.paid ? 'Mark Unpaid' : 'Mark Paid')}
          </span>
        </button>

        {!invoice.finalized && (
          <button className="action-panel-btn" onClick={() => finalizeAction.open(invoice.id)}>
            <div className="action-panel-icon">
              <FontAwesomeIcon icon={faCheck} />
            </div>
            <span className="action-panel-label">Finalize</span>
          </button>
        )}

        {!invoice.finalized && (
          <button className="action-panel-btn action-panel-btn-danger" onClick={() => deleteAction.open(invoice.id)}>
            <div className="action-panel-icon action-panel-icon-danger">
              <FontAwesomeIcon icon={faTrash} />
            </div>
            <span className="action-panel-label">Delete</span>
          </button>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteAction.isOpen}
        title="Delete Invoice"
        message="Are you sure you want to delete this invoice? This action cannot be undone."
        confirmLabel="Delete"
        confirmVariant="danger"
        onConfirm={deleteAction.confirm}
        onCancel={deleteAction.cancel}
        isLoading={deleteAction.isLoading}
      />

      {/* Finalize Confirmation Modal */}
      <ConfirmModal
        isOpen={finalizeAction.isOpen}
        title="Finalize Invoice"
        message="Once finalized, this invoice cannot be edited anymore. Only the payment status can be changed. Are you sure?"
        confirmLabel="Finalize"
        confirmVariant="primary"
        onConfirm={finalizeAction.confirm}
        onCancel={finalizeAction.cancel}
        isLoading={finalizeAction.isLoading}
      />
    </>
  );
};

export default InvoiceDetail;
